import WebpageService from '@modules/Webpages/services/webpage.service';
import PolicyService from '@modules/Policy/services/policy.service';
import { detectLivenessScripts } from '@shared/utils/livenessScripts';
import { createRuleKey, dedupeRules } from '@shared/utils/policyRules';

/**
 * Normalize rows from batch jobs, URL list, or domain list to { id, url }.
 */
export function normalizePageForSuggestPolicy(row) {
  const id = row.id != null ? row.id : row.webPageId;
  const url = row.url || row.webPageUrl || row.url_name || '';
  return { id, url };
}

function attachRuleSnippets(pageResult, rawRules, pageUrl, urlId) {
  return rawRules.map(rule => {
    let snippet = '';
    if (rule.field) {
      const htmlLines = pageResult.html.split('\n');
      const matchIndex = htmlLines.findIndex(line => line.toLowerCase().includes(rule.field.toLowerCase()));
      if (matchIndex !== -1) {
        const startLine = Math.max(0, matchIndex - 1);
        const endLine = Math.min(htmlLines.length - 1, matchIndex + 1);
        const snippetLines = [];
        for (let i = startLine; i <= endLine; i++) {
          let line = htmlLines[i];
          if (line.length > 300) {
            const idx = line.toLowerCase().indexOf(rule.field.toLowerCase());
            if (idx !== -1) {
              const start = Math.max(0, idx - 100);
              const end = Math.min(line.length, idx + 200);
              line = (start > 0 ? '...' : '') + line.substring(start, end) + (end < line.length ? '...' : '');
            } else {
              line = line.substring(0, 300) + '...';
            }
          }
          snippetLines.push(line);
        }
        snippet = snippetLines.join('\n');
      }
    }
    return {
      ...rule,
      occurrences: [{
        url: pageUrl || pageResult.url || `URL ID: ${urlId}`,
        snippet
      }]
    };
  });
}

/**
 * Fetches baseline HTML and policies, returns grouped suggestions for the modal.
 * @param {Array<{ id: *, url: string }>} normalizedPages
 * @returns {Promise<{ groups: Array, allPolicies: Array }>}
 */
export async function fetchSuggestedPolicyGroups(normalizedPages) {
  const urlIds = normalizedPages.map(p => p.id).filter(id => id != null);
  const [baselineResponse, policiesResponse] = await Promise.all([
    WebpageService.getBaselineHtml(urlIds),
    PolicyService.getList(),
  ]);

  const htmlResults = (baselineResponse && baselineResponse.results) || {};
  const policies = Array.isArray(policiesResponse) ? policiesResponse : [];
  const policyByUrlId = new Map();

  policies.forEach(policy => {
    (policy.urls || []).forEach(urlItem => {
      policyByUrlId.set(String(urlItem.id), policy);
    });
  });

  const newPolicyGroup = {
    mode: 'new',
    urls: [],
    urlIds: [],
    rules: [],
  };
  const newPolicyCollectedRules = [];

  const existingPolicyGroups = new Map();
  const existingPolicyCollections = new Map();
  const existingPolicyRuleKeys = new Map();

  normalizedPages.forEach(page => {
    const urlId = page.id;
    const urlIdKey = String(urlId);
    const pageUrl = page.url || '';
    const pageResult = htmlResults[urlId] || htmlResults[urlIdKey];

    if (!pageResult || !pageResult.html) return;

    const rawRules = detectLivenessScripts(pageResult.html, pageUrl || pageResult.url || '');
    const rulesWithSnippets = attachRuleSnippets(pageResult, rawRules, pageUrl, urlId);
    const detectedRules = dedupeRules(rulesWithSnippets);
    if (detectedRules.length === 0) return;

    const urlMeta = {
      id: urlId,
      url: pageUrl || pageResult.url || `URL ID: ${urlId}`,
    };

    const matchedPolicy = policyByUrlId.get(urlIdKey);
    if (!matchedPolicy) {
      newPolicyGroup.urls.push(urlMeta);
      newPolicyGroup.urlIds.push(urlId);
      for (let i = 0; i < detectedRules.length; i++) {
        newPolicyCollectedRules.push(detectedRules[i]);
      }
      return;
    }

    const policyKey = String(matchedPolicy.id);
    let currentGroup = existingPolicyGroups.get(policyKey);
    let collectedRules;
    if (!currentGroup) {
      currentGroup = {
        mode: 'existing',
        policyId: matchedPolicy.id,
        policyName: matchedPolicy.name || '',
        urls: [],
        rules: [],
        newRulesOnly: [],
      };
      collectedRules = [];
      existingPolicyGroups.set(policyKey, currentGroup);
      existingPolicyCollections.set(policyKey, collectedRules);
      existingPolicyRuleKeys.set(
        policyKey,
        new Set((matchedPolicy.rules || []).map(rule => createRuleKey(rule)))
      );
    } else {
      collectedRules = existingPolicyCollections.get(policyKey);
    }

    currentGroup.urls.push(urlMeta);
    for (let i = 0; i < detectedRules.length; i++) {
      collectedRules.push(detectedRules[i]);
    }
  });

  const groups = [];

  newPolicyGroup.rules = dedupeRules(newPolicyCollectedRules);
  if (newPolicyGroup.urls.length > 0 && newPolicyGroup.rules.length > 0) {
    newPolicyGroup.urlIds = Array.from(new Set(newPolicyGroup.urlIds));
    groups.push(newPolicyGroup);
  }

  existingPolicyGroups.forEach((group, policyKey) => {
    const collectedRules = existingPolicyCollections.get(policyKey) || [];
    const existingRuleKeys = existingPolicyRuleKeys.get(policyKey) || new Set();
    group.rules = dedupeRules(collectedRules);
    group.newRulesOnly = group.rules.filter(rule => !existingRuleKeys.has(createRuleKey(rule)));

    const uniqueUrls = [];
    const uniqueUrlSet = new Set();
    group.urls.forEach(url => {
      if (!uniqueUrlSet.has(String(url.id))) {
        uniqueUrlSet.add(String(url.id));
        uniqueUrls.push(url);
      }
    });
    group.urls = uniqueUrls;
    groups.push(group);
  });

  return { groups, allPolicies: policies };
}
